import { TypedDocumentNode, gql } from '@apollo/client';
import styled, { css } from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { isType } from '@sorare/core/src/gql';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useAmountWithConversion from '@sorare/core/src/hooks/useAmountWithConversion';
import { range } from '@sorare/core/src/lib/arrays';
import {
  MonetaryAmountParams,
  monetaryAmountFragment,
} from '@sorare/core/src/lib/monetaryAmount';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  StarUniqueAuctionBids,
  StarUniqueAuctionBidsVariables,
} from './__generated__/index.graphql';

const STAR_UNIQUE_AUCTIONS_BIDS_QUERY = gql`
  query StarUniqueAuctionBids($auctionId: String!) {
    tokens {
      auction(id: $auctionId) {
        id
        bids(first: 6) {
          nodes {
            id
            amounts {
              ...MonetaryAmountFragment_monetaryAmount
            }
            bidder {
              ... on User {
                slug
                nickname
              }
              ...MyClubLink_publicUserInfoInterface
              ...Avatar_publicUserInfoInterface
            }
          }
        }
      }
    }
  }
  ${monetaryAmountFragment}
  ${MyClubLink.fragments.user}
  ${Avatar.fragments.publicUserInfoInterface}
` as TypedDocumentNode<StarUniqueAuctionBids, StarUniqueAuctionBidsVariables>;

const Bidders = styled(Vertical).attrs({ gap: 1.5 })`
  overflow: hidden;
  transition: all 0.3s;
  max-width: min(33vw, 200px);
  width: 100%;
  @media ${laptopAndAbove} {
    width: auto;
    max-width: none;
    height: 0;
  }
  .expanded & {
    @media ${laptopAndAbove} {
      height: calc(31 * var(--unit));
    }
  }
`;

const Bidder = styled(Horizontal).attrs({ gap: 1 })`
  @media ${laptopAndAbove} {
    flex-direction: row-reverse;
    text-align: right;
  }
`;

const BidderMetas = styled(Vertical).attrs({ gap: 0.5 })`
  min-width: 0;
  @media ${laptopAndAbove} {
    text-align: right;
  }
`;

const BidderImg = styled(MyClubLink)`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  border-radius: 50%;
  filter: grayscale(1);
`;

const ellipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
  width: 100%;
`;
const Ellipsis = styled(BodyS)`
  ${ellipsis}
`;

const AmountEllipsis = styled(LabelS)`
  ${ellipsis}
`;

const BestBidder = styled(BodyS)`
  @media ${laptopAndAbove} {
    font: var(--t---t-body-l);
  }
`;

const SkeletonLine = styled(Skeleton)`
  width: 100%;
  height: calc(5 * var(--unit));
  @media ${laptopAndAbove} {
    width: min(200px, 100%);
    align-self: self-end;
  }
`;

type Props = {
  auctionId: string;
  expanded?: boolean;
};

const Amount = ({ amounts }: { amounts: MonetaryAmountParams }) => {
  const { main } = useAmountWithConversion({ monetaryAmount: amounts });
  return <AmountEllipsis color="var(--c-neutral-600)">{main}</AmountEllipsis>;
};

export const LoosingBids = ({ auctionId, expanded }: Props) => {
  const { up: isLaptopOrDesktop } = useScreenSize('laptop');
  const { data, loading } = useQuery(STAR_UNIQUE_AUCTIONS_BIDS_QUERY, {
    variables: { auctionId: idFromObject(auctionId) },
    skip: !expanded,
  });

  const bids = data?.tokens?.auction?.bids.nodes;

  if (loading)
    return (
      <Bidders>
        {range(isLaptopOrDesktop ? 5 : 2).map(i => (
          <SkeletonLine key={i} />
        ))}
      </Bidders>
    );

  const loosingBids = isLaptopOrDesktop ? bids?.slice(1, 6) : bids?.slice(1, 3);

  if (!loosingBids?.length) return null;

  return (
    <Bidders>
      {loosingBids.map(
        bid =>
          isType(bid.bidder, 'User') && (
            <Bidder key={bid.id}>
              <BidderImg user={bid.bidder}>
                <Avatar user={bid.bidder} variant="medium" rounded />
              </BidderImg>
              <BidderMetas>
                <Ellipsis>
                  <MyClubLink user={bid.bidder}>
                    <BestBidder as="span" color="var(--c-neutral-900)">
                      {bid.bidder.nickname}
                    </BestBidder>
                  </MyClubLink>
                </Ellipsis>
                <Amount amounts={bid.amounts} />
              </BidderMetas>
            </Bidder>
          )
      )}
    </Bidders>
  );
};
